import {dispatch, EVENT_ID, subscribe, unsubscribe} from '../../../../containers/event_manager';
import {projectUrl} from '../../../../directApi';
import {MOCK_AGENTS} from './mockAgents';
import {addNotification, isTaskCurrentlyViewed} from './novaNotificationStore';

function resolveAgentName(agentId?: string): string {
	const agent = MOCK_AGENTS.find(a => a.id === agentId);
	return agent?.title ?? MOCK_AGENTS.find(a => a.isDefault)?.title ?? 'General';
}

// 'workflow' matches the same project-landing-page slug the real notification system's own link
// builder falls back to (Notification.js); the /T<companyTaskId> suffix matches its established
// convention for auto-opening a specific task's modal on arrival. No customId is threaded through
// from callers, so this always uses projectUrl's own P-<id> fallback form — a real, working URL,
// just not the prettier custom-slug one.
function buildTaskUrl(companyProjectId?: string | number, companyTaskId?: string | number): string | undefined {
	if (!companyProjectId || !companyTaskId) return undefined;
	return `${projectUrl(companyProjectId, undefined)}/workflow/T${companyTaskId}`;
}

// Stand-in for the run service's real typed-failure taxonomy, which does not exist yet. Matches
// the PRD's own example causes. Replace with the real taxonomy once it ships — this is a value
// swap, not a shape change, since every consumer already switches on FailureCause by name.
export type FailureCause =
	| 'missing_access'
	| 'timeout'
	| 'unavailable_model'
	| 'permission'
	| 'document_processing'
	| 'no_deployed_version';

export type RunOutcome = {kind: 'succeeded'} | {kind: 'failed'; cause: FailureCause} | {kind: 'cancelled'};

export interface RunState {
	hasEverRun: boolean;
	isRunning: boolean;
	outputRead: boolean;
	outcome: RunOutcome | null;
}

const RUN_DURATION_MS = 10000;

const FAILURE_CAUSES: FailureCause[] = [
	'missing_access',
	'timeout',
	'unavailable_model',
	'permission',
	'document_processing',
	'no_deployed_version',
];

// Rare enough not to be the common case in manual testing, frequent enough to be seen without
// forcing it. Not tuned against any real failure rate — there isn't one to tune against yet.
const SIMULATED_FAILURE_RATE = 1 / 8;

function defaultPickOutcome(random: () => number = Math.random): RunOutcome {
	if (random() < SIMULATED_FAILURE_RATE) {
		const cause = FAILURE_CAUSES[Math.floor(random() * FAILURE_CAUSES.length)];
		return {kind: 'failed', cause};
	}
	return {kind: 'succeeded'};
}

// Once three tasks are running at once, every other idle control disables itself with a reason
// until one finishes. This is a client-simulated ceiling over this same store, not a real
// account-wide limit (that depends on a platform-side capacity check that doesn't exist yet) —
// it mirrors the mockup's own local running-count check.
export const RUN_CAP = 3;

const runStates = new Map<string, RunState>();
// Tracks each task's pending timer so stop() can cancel it — without this, a stop still overwrites
// the visible state, but the original timer fires anyway later and silently clobbers it back.
const runTimers = new Map<string, ReturnType<typeof setTimeout>>();

// Tracks how many tasks are currently running, kept in step with the two places below that flip
// isRunning, rather than scanning the whole map on every read — the map only grows across a
// session and this count is read on every mounted control's mount and on every state change.
let runningCount = 0;

function readState(taskId: string): RunState {
	return runStates.get(taskId) ?? {hasEverRun: false, isRunning: false, outputRead: false, outcome: null};
}

function writeState(taskId: string, next: RunState): void {
	runStates.set(taskId, next);
	dispatch(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, taskId);
}

export function getRunState(taskId: string): RunState {
	return readState(taskId);
}

export function getRunningCount(): number {
	return runningCount;
}

export function isAtRunCap(): boolean {
	return runningCount >= RUN_CAP;
}

// Stands in for the platform's real account-wide budget/capacity check, which doesn't exist yet.
// Derives the same near/at-limit shape from this local run-cap simulation — swap the body, not
// the callers, once a real account-level check exists.
export function getAccountLimitNotice(): 'near_limit' | 'at_limit' | null {
	if (runningCount >= RUN_CAP) return 'at_limit';
	if (runningCount === RUN_CAP - 1) return 'near_limit';
	return null;
}

export function invoke(
	taskId: string,
	agentId?: string,
	opts?: {
		pickOutcome?: () => RunOutcome;
		taskName?: string;
		companyProjectId?: string | number;
		companyTaskId?: string | number;
		projectColor?: string;
	}
): void {
	// agentId varies which agent resolves for the notification's name below, but this simulation
	// does not otherwise vary run behavior by agent; every run completes the same way.
	const current = readState(taskId);
	// A second call for a task already mid-run would otherwise start a second timer; that timer's
	// completion later overwrites state unconditionally and could silently revert a read output
	// back to unread. No caller triggers this today, but the guard costs nothing.
	if (current.isRunning) return;
	// The cap is also enforced presentationally in deriveControlState (every other idle control
	// disables once isAtRunCap() is true), but that's the UI's rendering of the invariant, not
	// the invariant itself — NovaAgentPickerForTask calls invoke() directly, bypassing that
	// disabled-button gate entirely. Not reachable today (the caret that opens the picker is
	// hidden outside the 'action' state), but the store, not the UI, is where this belongs once
	// a second call site exists.
	if (isAtRunCap()) return;
	runningCount += 1;
	writeState(taskId, {...current, isRunning: true, outcome: null});
	// No run service exists yet, so this timer stands in for one. Swapping in a real network call
	// still means replacing this body. pickOutcome is injectable so callers/tests can force a
	// specific branch deterministically; the default introduces SIMULATED_FAILURE_RATE so failure
	// presentation is reachable without forcing it.
	const pickOutcome = opts?.pickOutcome ?? defaultPickOutcome;
	const timer = setTimeout(() => {
		runningCount -= 1;
		const outcome = pickOutcome();
		writeState(taskId, {
			// Only a real answer counts as "ever run" for Ready/Continue purposes — a failed run
			// has nothing to review, so it must not read as Ready. (A cancelled run never reaches
			// this timer body at all; stop() below writes its own terminal state directly.)
			hasEverRun: outcome.kind === 'succeeded',
			isRunning: false,
			outputRead: false,
			outcome,
		});
		runTimers.delete(taskId);
		if ((outcome.kind === 'succeeded' || outcome.kind === 'failed') && !isTaskCurrentlyViewed(taskId)) {
			addNotification({
				taskId,
				taskName: opts?.taskName ?? taskId,
				agentName: resolveAgentName(agentId),
				kind: outcome.kind,
				taskUrl: buildTaskUrl(opts?.companyProjectId, opts?.companyTaskId),
				companyTaskId: opts?.companyTaskId,
				projectColor: opts?.projectColor,
				companyProjectId: opts?.companyProjectId,
			});
		}
	}, RUN_DURATION_MS);
	runTimers.set(taskId, timer);
}

export function stop(taskId: string): void {
	const current = readState(taskId);
	if (!current.isRunning) return;
	const timer = runTimers.get(taskId);
	if (timer) clearTimeout(timer);
	runTimers.delete(taskId);
	runningCount -= 1;
	writeState(taskId, {hasEverRun: current.hasEverRun, isRunning: false, outputRead: false, outcome: {kind: 'cancelled'}});
}

export function markRead(taskId: string): void {
	const current = readState(taskId);
	if (!current.hasEverRun || current.isRunning) return;
	writeState(taskId, {...current, outputRead: true});
}

export function subscribeToRunState(taskId: string, callback: () => void): () => void {
	const handler = (changedTaskId: string): void => {
		if (changedTaskId === taskId) callback();
	};
	subscribe<string>(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, handler);
	return () => unsubscribe<string>(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, handler);
}

// The run cap and the row-highlight-on-unread-Ready feature are both properties of the whole
// tracked set, not of one taskId — a plain per-task subscription can't tell an idle task that a
// caller renders elsewhere just now hit (or dropped below) the ceiling.
export function subscribeToAnyRunStateChange(callback: () => void): () => void {
	const handler = (): void => callback();
	subscribe<string>(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, handler);
	return () => unsubscribe<string>(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, handler);
}

export function isUnreadReady(taskId: string): boolean {
	const state = readState(taskId);
	// hasEverRun can be stale-true from an earlier success carried through a later re-invoke (see
	// invoke()'s spread of `current`) — a failed/cancelled outcome on that later attempt means
	// there is no new output to flag as ready, regardless of what an older run left behind.
	const outcomeBlocksReady = state.outcome?.kind === 'failed' || state.outcome?.kind === 'cancelled';
	return state.hasEverRun && !state.isRunning && !state.outputRead && !outcomeBlocksReady;
}
